import * as ngCore from '@angular/core'
import { TypeChecker } from '@attain-apps/type-guards'
import { ComponentStore } from '@ngrx/component-store'

import type * as models from '../models'

export interface State<TValue> {
  config: models.Config
  disabled: boolean
  error?: string | Error
  expanded: boolean
  isLoading: boolean
  isOpen: boolean
  items: readonly models.Item<TValue>[]
  label?: string
  placeholder?: string
  searchValue?: string
  selectedValues: readonly models.Item<TValue>['value'][]
}
export namespace State {
  export const INITIAL: NoMissing<State<any>> = {
    config: {
      overlay: {
        maxHeight: 260,
        minSearchCharacters: 2,
      },
    },
    disabled: false,
    error: undefined,
    expanded: false,
    isLoading: false,
    isOpen: false,
    items: [],
    label: '',
    placeholder: undefined,
    searchValue: '',
    selectedValues: [],
  }
}

@ngCore.Injectable()
export class AtnDsSelectMultipleStore<TValue> extends ComponentStore<
  State<TValue>
> {
  /** @see {@link State.config} */
  public readonly configSg = this.selectSignal(state => state.config)
  /** @see {@link State.selectedValues} */
  public readonly selectedValuesSg = this.selectSignal(
    state => state.selectedValues ?? [],
  )
  /** @see {@link State.label} */
  public readonly labelSg = this.selectSignal(state => state.label)
  /** @see {@link State.items} */
  public readonly itemsSg = this.selectSignal(state => state.items)
  /** @see {@link State.isLoading} */
  public readonly isLoadingSg = this.selectSignal(state => state.isLoading)
  /** @see {@link State.disabled} */
  public readonly disabledSg = this.selectSignal(state => state.disabled)
  /** @see {@link State.placeholder} */
  public readonly placeholderSg = this.selectSignal(state => state.placeholder)
  /**
   * Use {@link presentableErrorSg} instead.
   *
   * @see {@link State.error}
   */
  private readonly _errorSg = this.selectSignal(state => state.error)
  /** Presents the current error in a user-friendly way */
  public readonly presentableErrorSg = ngCore.computed(
    (error = this._errorSg()) =>
      // If error exist, check if its a string or an Error and return the
      // appropriate message.
      error ? (TypeChecker.isString(error) ? error : error.message) : undefined,
  )
  /** @see {@link State.expanded} */
  public readonly expandedSg = this.selectSignal(state => state.expanded)
  /** @see {@link State.isOpen} */
  public readonly isOpenSg = this.selectSignal(state => state.isOpen)
  /** @see {@link State.searchValue} */
  public readonly searchValueSg = this.selectSignal(state => state.searchValue)
  /** @see {@link models.Config.overlay} */
  public readonly configOverlaySg = ngCore.computed(
    (config = this.configSg()) => config.overlay ?? {},
  )

  /** TODO:  */
  public readonly valuesSg = ngCore.computed((items = this.itemsSg()) =>
    items.map(item => item.value),
  )

  /** TODO:  */
  public readonly filteredItemsSg = ngCore.computed(
    (
      items = this.itemsSg(),
      searchValue = this.searchValueSg(),
      configOverlay = this.configOverlaySg(),
    ) =>
      searchValue?.length &&
      searchValue?.length >= (configOverlay.minSearchCharacters ?? 0)
        ? items.filter(item =>
            AtnDsSelectMultipleStore.filterPredicate(item, searchValue),
          )
        : items,
  )

  /** TODO:  */
  public readonly selectedItemsSg = ngCore.computed(
    (items = this.itemsSg(), selectedValues = this.selectedValuesSg()) =>
      // TODO: value can be undefined, what to do in that case?
      // Maybe move "item" discovery to models and use same approach in single and multiselect
      items.filter(item => selectedValues.includes(item.value)),
  )

  /** Count of {@link itemsSg} */
  public readonly totalCountSg = ngCore.computed(
    (items = this.itemsSg()) => items.length,
  )

  /** Count of {@link filteredItemsSg} */
  public readonly filteredCountSg = ngCore.computed(
    (filteredItems = this.filteredItemsSg()) => filteredItems.length,
  )

  /** Count of {@link selectedValuesSg} */
  public readonly selectedCountSg = ngCore.computed(
    (selectedValues = this.selectedValuesSg()) => selectedValues.length,
  )

  constructor() {
    super(State.INITIAL)
  }

  /** TODO:  */
  public removeSelectedValues(
    valueToRemove: models.Item<TValue>['value'],
  ): void {
    // skip removal when value is undefined since it's a possible type for
    // unknown
    if (valueToRemove === undefined) {
      return
    }

    const selectedValues = this.selectedValuesSg()
    const newSelectedValues = selectedValues.filter(
      selectedValue => selectedValue !== valueToRemove,
    )

    this.patchState({ selectedValues: newSelectedValues })
  }

  /** TODO:  */
  public putSelectedValues(valueToPut: models.Item<TValue>['value']): void {
    // skip removal when value is undefined since it's a possible type for
    // unknown
    if (valueToPut === undefined) {
      return
    }

    const selectedValues = this.selectedValuesSg()
    this.patchState({ selectedValues: [...selectedValues, valueToPut] })
  }
}

export namespace AtnDsSelectMultipleStore {
  export function filterPredicate(
    item: models.Item,
    searchTerm: string,
  ): boolean {
    return item.label
      .trim()
      .toLowerCase()
      .includes(searchTerm.trim().toLowerCase())
  }
}
